import React, { useState, useEffect, useMemo, createRef, useRef } from 'react';
import PropTypes from 'prop-types';

import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { Chip } from 'components/v3';

import { useStyles, defaultContainerHeight } from './TagsContainer.styles';

const pixelToNumber = value => parseInt(value.replace('px', ''));

const TagsContainer = ({ items, onDeleteClick }) => {
  const { classes: styles } = useStyles();

  const chipListWidthRef = useRef(null);
  const limitTagRef = useRef(null);
  const chipRefs = {};

  const [expanded, setExpanded] = useState(false);
  const [hiddenChipsCount, setHiddenChipsCount] = useState(0);
  const [chipMaxWidth, setChipMaxWidth] = useState(null);

  const formattedCount = useMemo(() => {
    if (expanded) return <ExpandLessIcon classes={{ root: styles.root }} />;
    if (hiddenChipsCount > 99) return '99+';
    return `+${hiddenChipsCount}`;
  }, [hiddenChipsCount, expanded]);

  const showLimitTag = useMemo(() => hiddenChipsCount > 0, [hiddenChipsCount]);

  const toggleExpand = () => setExpanded(prev => !prev);
  const closeExpand = () => expanded && setExpanded(false);
  const openExpand = () => hiddenChipsCount > 0 && !expanded && setExpanded(true);

  useEffect(() => {
    let count = 0;
    Object.values(chipRefs).forEach(chipRef => {
      const chipOffset = chipRef.current.offsetTop;
      if (chipOffset > defaultContainerHeight) {
        count++;
      }
    });

    setHiddenChipsCount(count);
  }, [chipRefs]);

  useEffect(() => {
    if (chipListWidthRef.current) {
      const chipListComputedStyle = window.getComputedStyle(chipListWidthRef.current);
      const chipListWidth = pixelToNumber(chipListComputedStyle.width);
      const chipListPaddingLeft = pixelToNumber(chipListComputedStyle.paddingLeft);
      const chipListPaddingRight = pixelToNumber(chipListComputedStyle.paddingRight);
      const chipListCompensation = chipListWidth - chipListPaddingLeft - chipListPaddingRight;

      let limitTagCompensation = 0;
      if (showLimitTag && limitTagRef.current) {
        const limitTagComputedStyle = window.getComputedStyle(limitTagRef.current);
        const limitTagMarginLeft = pixelToNumber(limitTagComputedStyle.marginLeft);
        const limitTagMarginRight = pixelToNumber(limitTagComputedStyle.marginRight);
        const limitTagWidth = pixelToNumber(limitTagComputedStyle.width);
        limitTagCompensation = limitTagWidth + limitTagMarginLeft + limitTagMarginRight;
      }

      // we calculate the sizes of the component so that first chip has a max width
      // that makes it fit next of the limitTag, instead of pushing it below.
      // We also add a "security" 5 pixels just to make sure.
      const result = chipListCompensation - limitTagCompensation - 5;

      setChipMaxWidth(result);
    }
  }, [chipListWidthRef.current, showLimitTag, limitTagRef.current]);

  return (
    <div className={styles.outerContainer}>
      <ClickAwayListener onClickAway={closeExpand}>
        <div onClick={openExpand} className={`${styles.mainContainer} ${expanded ? styles.mainContainerExpanded : ''}`}>
          <div ref={chipListWidthRef} className={`${styles.chipList} ${expanded ? styles.chipListExpanded : ''}`}>
            {showLimitTag && (
              <div onClick={toggleExpand} ref={limitTagRef} className={styles.limitTag}>
                {formattedCount}
              </div>
            )}
            {items.map((item, chipIndex) => {
              const newRef = `chip_${chipIndex}`;
              chipRefs[newRef] = createRef();
              return (
                <div key={newRef} className={styles.tagContainer}>
                  <Chip
                    greyed
                    ref={chipRefs[newRef]}
                    label={item.label}
                    onClose={() => onDeleteClick(item)}
                    {...(chipMaxWidth ? { maxWidth: chipMaxWidth } : {})}
                  />
                </div>
              );
            })}
            <div className={styles.clear} />
          </div>
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default TagsContainer;

TagsContainer.propTypes = {
  items: PropTypes.array.isRequired,
  onDeleteClick: PropTypes.func.isRequired
};
